import { apiErrorMessage } from '@agos/api-client'
import { usePostRevenueHandovers } from '@agos/api-client/payments'
import { T, formatCents, tt } from '@agos/shared'
import { Spinner, cn, useToast } from '@agos/ui'
import { X } from 'lucide-react'
import { useState } from 'react'

import {
  FIELDS,
  cardMismatchNote,
  computeHandover,
  derivationNote,
  handoverPayload,
  maySubmit,
  prefill,
  problems,
  recordedNote,
  refusalNote,
  shortfallNote,
  type Draft,
  type Notes,
} from './handover'
import type { DayTotals } from './wallet'

/**
 * The takings calculator, as a sheet over the wallet.
 *
 * Opens on a **day**, never on *Všetko*: without `forDate` the server assumes today, and a driver
 * settling yesterday's shift after midnight would hand over against the wrong day's orders.
 *
 * `POST /revenue-handovers` is `@RequireAuth({"driver", "waiter"})` — an administrator may read this
 * wallet and may not submit against it. FB-001's rule, in {@link maySubmit}: admit whoever the server lets
 * read, disable what it will not let them write, and say why.
 */
export function HandoverSheet(props: {
  totals: DayTotals
  day: string | null
  dayName: string
  systemRole: string | undefined
  onClose: () => void
}) {
  const [draft, setDraft] = useState<Draft>(() => prefill(props.totals))
  const [notes, setNotes] = useState<Notes>({ payout1: '', payout2: '' })
  const { toast } = useToast()

  const record = usePostRevenueHandovers({
    mutation: {
      onSuccess: () => {
        toast({ title: tt('Tržba odovzdaná'), description: recordedNote() })
        props.onClose()
      },
      // §9.385 - money leaving a driver's pocket. The handover endpoint refuses by name: a day
      // already closed, a figure that does not reconcile.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Tržbu sa nepodarilo odovzdať'),
          description: apiErrorMessage(caught, tt('Server odovzdanie odmietol.')),
        }),
    },
  })

  const handover = computeHandover(props.totals, draft)
  const found = problems(draft, props.day)
  const mayWrite = maySubmit(props.systemRole)
  const mismatch = cardMismatchNote(props.totals, draft)
  const shortfall = shortfallNote(handover)

  const set = (field: keyof Draft, value: string) => setDraft((old) => ({ ...old, [field]: value }))

  return (
    <div className="fixed inset-0 z-50 flex items-end" data-handover-sheet>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label={tt('Zavrieť')}
        onClick={props.onClose}
      />
      <section className="relative w-full max-h-[92dvh] overflow-y-auto rounded-t-3xl bg-background border-t border-border p-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-bold">
            <T>Kalkulačka tržby</T> · {props.dayName}
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label={tt('Zavrieť')}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="rounded-2xl bg-card border border-border p-3 space-y-1">
          <p className="text-xs text-muted-foreground">
            <T>Tržba za deň</T>
          </p>
          <p className="text-lg font-bold tabular-nums" data-calc-revenue>
            {formatCents(handover.revenueCents)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            <T>Online platby sa odrátajú automaticky</T>: {formatCents(handover.onlineCents)}
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground">
            <T>Odrátať</T>
          </p>
          {FIELDS.map(({ field, label, note }) => (
            <div key={field} className="space-y-1">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <input
                  inputMode="decimal"
                  value={draft[field]}
                  onChange={(event) => set(field, event.target.value)}
                  placeholder="0,00"
                  data-calc-field={field}
                  className="w-32 min-h-[44px] rounded-xl bg-card border border-border px-3 text-right tabular-nums outline-none focus:border-primary"
                />
              </label>
              {note === true && (
                <input
                  value={notes[field === 'payout1' ? 'payout1' : 'payout2']}
                  onChange={(event) =>
                    setNotes((old) => ({
                      ...old,
                      [field === 'payout1' ? 'payout1' : 'payout2']: event.target.value,
                    }))
                  }
                  placeholder={tt('Za čo (nepovinné)')}
                  maxLength={500}
                  data-calc-note={field}
                  className="w-full min-h-[40px] rounded-xl bg-card border border-border px-3 text-xs outline-none focus:border-primary"
                />
              )}
            </div>
          ))}
        </div>

        {mismatch !== '' && (
          <p className="text-[11px] text-amber-400/90" data-calc-mismatch>
            {mismatch}
          </p>
        )}

        <div className="rounded-2xl bg-card border border-border p-3 space-y-1">
          <p className="text-xs text-muted-foreground">
            <T>Odovzdávate v hotovosti</T>
          </p>
          <p className="text-2xl font-bold tabular-nums" data-calc-result>
            {formatCents(handover.cashToHandOverCents)}
          </p>
          {/* The working, so the figure can be retraced without opening a calculator of your own. */}
          <p className="text-[11px] text-muted-foreground" data-calc-derivation>
            {derivationNote(handover)}
          </p>
        </div>

        {shortfall !== '' && (
          <p className="text-[11px] text-amber-400/90" data-calc-shortfall>
            {shortfall}
          </p>
        )}
        {found.length > 0 && (
          <ul className="text-[11px] text-destructive space-y-1" data-calc-problems>
            {found.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
        {!mayWrite && (
          <p className="text-[11px] text-muted-foreground" data-calc-readonly>
            {refusalNote()}
          </p>
        )}

        <button
          type="button"
          data-calc-submit
          disabled={!mayWrite || found.length > 0 || record.isPending || props.day === null}
          onClick={() => {
            if (props.day === null) return
            record.mutate({
              data: handoverPayload(props.totals, draft, notes, props.day) as never,
            })
          }}
          className={cn(
            'w-full min-h-[52px] rounded-2xl bg-primary text-primary-foreground font-bold',
            'hover:opacity-90 transition-opacity disabled:opacity-50',
          )}
        >
          {record.isPending ? <Spinner /> : <T>Odovzdať tržbu</T>}
        </button>
      </section>
    </div>
  )
}
